import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BillingModal from "../components/BillingModal";
import EuPagoModal from "../components/EuPagoModal";
import { useAuth } from "../context/AuthContext";
import { Crown, Sparkles, Check, X, Receipt, Tag } from "lucide-react";
import { toast } from "sonner";
import {
  TokensExplainer, ComparisonTable, AffinityTiers,
  WorkshopCoachingCta, PremiumFaq,
} from "../components/premium/PremiumStaticSections";

const isBillingComplete = (bp) => {
  if (!bp) return false;
  const required = ["name", "address", "postal_code", "city", "country"];
  return required.every((k) => (bp[k] || "").toString().trim().length > 0);
};

const Premium = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [pendingPkg, setPendingPkg] = useState(null);
  const [billingProfile, setBillingProfile] = useState(null);
  const [coupon, setCoupon] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState(null);
  // Legacy state (kept for API stability with sub-components) — we no longer
  // offer Stripe recurring subscriptions in the UI, so this is always
  // "payment" (one-off via EuPago). See v60.10 removal of Stripe.
  const [eupagoOpen, setEupagoOpen] = useState(false);
  const [eupagoCtx, setEupagoCtx] = useState(null);  // {package_id, amount, currency, label, billing, coupon}

  useEffect(() => {
    (async () => {
      try {
        const res = await client.get("/payments/packages");
        setPackages(res.data.packages);
      } catch (err) {
        console.warn("[Premium] package list fetch failed", err);
      }
    })();
    // Pre-load billing profile so we can decide whether the modal is mandatory.
    (async () => {
      try {
        const r = await client.get("/billing-profile");
        setBillingProfile(r.data.billing_profile || null);
      } catch (err) {
        // 404 is expected when user has never set up billing; log others.
        if (err?.response?.status !== 404) {
          console.warn("[Premium] billing-profile fetch failed", err);
        }
      }
    })();
    const code = params.get("coupon");
    if (code) applyCoupon(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  const applyCoupon = async (code) => {
    setCouponError(null);
    if (!code) { setCoupon(null); return; }
    try {
      const r = await client.get(`/coupons/${encodeURIComponent(code.trim().toUpperCase())}`);
      setCoupon(r.data);
      setCouponInput(r.data.code);
      toast.success(`Cupão ${r.data.code} aplicado: ${Math.round(r.data.percent_off || 0)}% off`);
    } catch (err) {
      setCoupon(null);
      setCouponError(err?.response?.data?.detail || "Cupão inválido.");
    }
  };

  const startCheckout = (pkgId) => {
    setPendingPkg(pkgId);
    setBillingOpen(true);
  };

  const onBillingConfirmed = async (billing) => {
    setBillingOpen(false);
    setBillingProfile(billing);
    const pkgId = pendingPkg;
    // All purchases now go through EuPago (Multibanco / MBWAY).
    // Stripe is no longer a supported payment method in the UI.
    const pkg = packages.find((p) => p.id === pkgId);
    if (!pkg) {
      toast.error("Pacote não disponível.");
      return;
    }
    const pct = coupon?.percent_off || 0;
    const finalAmount = pct ? pkg.amount * (1 - pct / 100) : pkg.amount;
    setEupagoCtx({
      package_id: pkgId,
      amount: finalAmount,
      currency: pkg.currency,
      label: pkg.label,
      billing,
      coupon: coupon?.code || null,
    });
    setEupagoOpen(true);
  };

  const cell = (val) => {
    if (val === true)  return <Check className="w-5 h-5 text-[#C5A880] mx-auto" />;
    if (val === false) return <X className="w-5 h-5 text-white/15 mx-auto" />;
    return <span className="text-sm text-[#DCD7D2]">{val}</span>;
  };

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="premium-page">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/40 text-[#C5A880] text-xs uppercase tracking-wider mb-6">
            <Crown className="w-4 h-4" /> Premium Soulmatch
          </div>
          <h1 className="font-display text-4xl md:text-6xl leading-tight mb-5">
            Vê <em className="text-[#C5A880]">com mais profundidade</em>.
            <br />Decide com mais segurança.
          </h1>
          <p className="text-[#DCD7D2] text-lg leading-relaxed">
            A Soulmatch é gratuita para encontrares ligações. O Premium dá-te ferramentas profissionais
            de validação para conheceres o carácter por detrás do perfil — antes do 1º encontro.
          </p>
        </div>

        {/* Coupon input */}
        <div className="max-w-md mx-auto mb-6" data-testid="coupon-input-wrap">
          <div className="flex items-center gap-2 bg-[#161920] border border-white/10 rounded-2xl px-4 py-2">
            <Tag className="w-4 h-4 text-[#C5A880] shrink-0" />
            <input
              value={couponInput}
              onChange={(e)=>setCouponInput(e.target.value.toUpperCase())}
              onKeyDown={(e)=>{ if (e.key === "Enter") applyCoupon(couponInput); }}
              placeholder="Cupão de desconto (opcional)"
              className="bg-transparent border-0 outline-none flex-1 text-[#F5F2EE] text-sm placeholder:text-[#A29F9B] uppercase"
              data-testid="coupon-input"
            />
            {coupon ? (
              <button onClick={()=>{ setCoupon(null); setCouponInput(""); setCouponError(null); }}
                className="text-xs text-[#A29F9B] hover:text-[#C5A880]"
                data-testid="coupon-clear">Remover</button>
            ) : (
              <button onClick={()=>applyCoupon(couponInput)}
                className="text-xs text-[#C5A880] hover:underline"
                data-testid="coupon-apply">Aplicar</button>
            )}
          </div>
          {couponError && (
            <p className="text-xs text-[#E37B7B] mt-2 px-2" data-testid="coupon-error">{couponError}</p>
          )}
        </div>

        {/* Payment methods hint — one-off only (Stripe subscriptions retired) */}
        <p className="text-center text-xs text-[#A29F9B] mb-10 max-w-xl mx-auto">
          Pagas uma vez por <strong className="text-[#C5A880]">MB WAY</strong> ou <strong className="text-[#C5A880]">Multibanco</strong>. No fim do período decides se renovas.
        </p>

        {/* Plans — one-off packages only (recurring Stripe subscriptions removed in v60.10) */}
        <div className="grid md:grid-cols-2 gap-6 mb-16" data-testid="premium-plans">
          {packages.filter((p) => (p.mode || "subscription") === "payment").map((p) => {
            const isAnnual = p.id === "annual" || p.id === "one_off_year";
            const monthlyEq = isAnnual ? `~${(p.amount / 12).toFixed(2)} €/mês` : null;
            const pct = coupon?.percent_off || 0;
            const finalAmount = pct ? p.amount * (1 - pct / 100) : p.amount;
            return (
              <div key={p.id}
                className={`p-8 rounded-2xl border ${isAnnual ? "bg-gradient-to-br from-[#C5A880]/10 to-[#161920] border-[#C5A880]" : "bg-[#161920] border-white/10"}`}
                data-testid={`plan-${p.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="label-uppercase">{isAnnual ? "Mais popular" : "Plano"}</span>
                    <h2 className="font-display text-3xl mt-2">{p.label}</h2>
                  </div>
                  {isAnnual && (
                    <span className="px-2 py-1 rounded-full bg-[#C5A880] text-[#0C0E12] text-[10px] font-semibold uppercase">-37%</span>
                  )}
                </div>
                <div className="mb-6">
                  {pct > 0 ? (
                    <>
                      <div className="flex items-baseline gap-3">
                        <span className="font-display text-5xl text-[#C5A880]">{finalAmount.toFixed(2)}</span>
                        <span className="text-[#A29F9B] line-through text-xl">{p.amount.toFixed(2)}</span>
                        <span className="text-[#A29F9B]">{p.currency.toUpperCase()}</span>
                      </div>
                      <p className="text-xs text-[#C5A880] mt-1 inline-flex items-center gap-1">
                        <Tag className="w-3 h-3" /> Cupão {coupon.code} aplicado · {Math.round(pct)}% off
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="font-display text-5xl text-[#F5F2EE]">{p.amount.toFixed(2)}</span>
                      <span className="text-[#A29F9B]"> {p.currency.toUpperCase()}</span>
                      {monthlyEq && <p className="text-xs text-[#A29F9B] mt-1">{monthlyEq}</p>}
                    </>
                  )}
                </div>
                <button onClick={()=>startCheckout(p.id)} disabled={busy === p.id}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2"
                  data-testid={`subscribe-${p.id}`}>
                  <Sparkles className="w-4 h-4" /> {busy === p.id ? "A redireccionar..." : (p.mode === "payment" ? "Comprar" : "Subscrever")}
                </button>
                <p className="text-xs text-[#A29F9B] mt-3 text-center inline-flex items-center justify-center gap-1.5 w-full">
                  <Receipt className="w-3 h-3" />
                  {p.mode === "payment"
                    ? "Pagamento único · Fatura+Recibo via Moloni"
                    : "Renovação automática · Fatura+Recibo via Moloni"}
                </p>
              </div>
            );
          })}
        </div>

        {/* Discovery tokens explainer (Free tier) */}
        <TokensExplainer />

        {/* Comparison table */}
        <ComparisonTable />

        {/* Affinity tiers explainer */}
        <AffinityTiers />

        {/* Workshop + Coaching CTA */}
        <WorkshopCoachingCta />

        {/* FAQ */}
        <PremiumFaq />

        <p className="text-center text-xs text-[#A29F9B] max-w-2xl mx-auto">
          Pagamento seguro em ambiente <strong className="text-[#DCD7D2]">EuPago</strong> (certificado SIBS · PSD2).
          {" "}Toda a venda gera <strong className="text-[#DCD7D2]">Fatura+Recibo</strong> Solfut, Lda via Moloni —
          {" "}disponível em <a className="text-[#C5A880] hover:underline" href="/faturacao">Faturação</a>.
        </p>
      </main>

      <BillingModal
        open={billingOpen}
        user={user}
        required={!isBillingComplete(billingProfile)}
        onClose={()=>{ setBillingOpen(false); setPendingPkg(null); }}
        onConfirm={onBillingConfirmed}
      />
      <EuPagoModal
        open={eupagoOpen}
        onClose={() => { setEupagoOpen(false); setEupagoCtx(null); }}
        packageId={eupagoCtx?.package_id}
        amount={eupagoCtx?.amount}
        currency={eupagoCtx?.currency}
        label={eupagoCtx?.label}
        billing={eupagoCtx?.billing}
        coupon={eupagoCtx?.coupon}
        onPaid={() => { navigate("/billing?from=eupago"); }}
      />
      <Footer />
    </div>
  );
};

export default Premium;
